import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useOrder } from '../context/OrderContext';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { useCart } from '../context/CartContext';
import { loadMenuItems, loadCategories, deriveBrandFromCategory, MenuItem, Category } from '../services/menuService';
import {
  BranchSchedule, DEFAULT_SCHEDULE, DAY_KEYS,
  subscribeBranchSchedule, computeStatus, nextOverride,
  formatOverrideDate, dayLabel, slotLines, formatSlots,
} from '../services/branchSchedule';
import SushiMarkIcon from '../components/SushiMarkIcon';
import { currentBranchId, setCurrentBranchId, branchContact } from '../config/branchContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';

// Restaurant location: Sudetenstr. 60, 50354 Hürth
const RESTAURANT_LAT = 50.8825;
const RESTAURANT_LNG = 6.8647;

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function estimateDeliveryMin(km: number): string {
  // Guard against nonsense distances (user outside delivery area or bad GPS).
  // SushiKi delivers ~Hürth only — anything >20km isn't a real delivery ETA.
  if (!isFinite(km) || km < 0 || km > 20) return '30-45';
  const low = Math.round(15 + km * 2);
  const high = Math.round(low + 10);
  return `${low}-${high}`;
}


// ─── Reveal-on-scroll hook ──────────────────────────
function useReveal() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

// ─── Main component ────────────────────────────────
const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();
  const { setMode } = useOrder();
  const { user, profile } = useAuth();
  const { brand, other, switchTo } = useBrand();
  const { addItem } = useCart();
  const langKey = lang === 'de' ? 'de' : 'en';

  const [branch, setBranch] = useState<string>(() => currentBranchId());
  const selectBranch = (b: string) => { setBranch(b); setCurrentBranchId(b); };
  // The brand texts say "Hürth" (the first branch). Show the selected
  // branch's city instead, so a Cäsarstraße or Köln customer does not read
  // "Japanische Küche · Hürth" (19 Oct 2026, Cäsarstraße webshop go-live).
  const branchCity = ({ koeln: 'Köln', caesarstrasse: 'Köln', sanktaugustin: 'Sankt Augustin' } as Record<string, string>)[branch] || 'Hürth';
  const localCity = (s: string) => s.replace(/Hürth/g, branchCity);
  // Branch toggle: Hürth + Köln as before, plus any other SushiKi branch once
  // it is switched to accepting orders (branches/{id}.is_accepting_orders).
  // Keeps Cäsarstraße / Sankt Augustin hidden until they go live.
  const [shopBranches, setShopBranches] = useState<string[]>(['huerth', 'koeln']);
  useEffect(() => {
    getDocs(collection(db, 'branches')).then((snap) => {
      const live = snap.docs
        .filter((d) => ['caesarstrasse', 'sanktaugustin'].includes(d.id) && d.data().is_accepting_orders === true)
        .map((d) => d.id);
      const shown = live.length ? ['huerth', 'koeln', ...live] : ['huerth', 'koeln'];
      if (live.length) setShopBranches(shown);
      // Direct branch link (Sadik 2026-10-12): sushiki.de will forward to
      // ?filiale=caesarstrasse so the shop opens on that branch. Only honoured
      // for a branch that is shown (i.e. accepting orders) — never pre-selects
      // a branch that is not live yet.
      try {
        const q = new URLSearchParams(window.location.search);
        const wanted = (q.get('filiale') || q.get('branch') || '').toLowerCase().trim();
        if (wanted && shown.includes(wanted)) selectBranch(wanted);
      } catch { /* ignore malformed URLs */ }
    }).catch(() => { /* keep defaults */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const contact = branchContact(branch);

  const [, setDistanceKm] = useState<number | null>(null);
  const [deliveryTime, setDeliveryTime] = useState('30-45');
  const [scrolled, setScrolled] = useState(false);
  const [sublineIdx, setSublineIdx] = useState(0);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [schedule, setSchedule] = useState<BranchSchedule>(DEFAULT_SCHEDULE);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => subscribeBranchSchedule(setSchedule), []);
  const openState = computeStatus(schedule, now);
  const upcomingClosure = nextOverride(schedule, 14, now);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([loadMenuItems(), loadCategories()]).then((results) => {
      if (cancelled) return;
      const [itemsRes, catsRes] = results;
      if (itemsRes.status === 'fulfilled') setItems(itemsRes.value as MenuItem[]);
      if (catsRes.status === 'fulfilled') setCategories(catsRes.value as Category[]);
      setDataLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    document.documentElement.lang = langKey;
    document.title = brand.metaTitle[langKey];
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', brand.metaDescription[langKey]);
    document.body.style.background = brand.bg;
  }, [brand, langKey]);

  // scroll state for sticky header
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // rotate sub-tagline
  useEffect(() => {
    const t = setInterval(() => setSublineIdx((i) => (i + 1) % brand.subTagline[langKey].length), 3800);
    return () => clearInterval(t);
  }, [brand, langKey]);

  // geolocation
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const km = haversineKm(pos.coords.latitude, pos.coords.longitude, RESTAURANT_LAT, RESTAURANT_LNG);
        setDistanceKm(Math.round(km * 10) / 10);
        setDeliveryTime(estimateDeliveryMin(km));
      },
      () => {},
      { enableHighAccuracy: false, timeout: 5000 },
    );
  }, []);

  const goToMenu = (mode: 'delivery' | 'pickup') => {
    setMode(mode);
    navigate(`/menu/${brand.id}`);
  };

  // Reveal refs for sections
  const uspReveal = useReveal();
  const categoriesReveal = useReveal();
  const storyReveal = useReveal();
  const gridReveal = useReveal();
  const reviewsReveal = useReveal();
  const promoReveal = useReveal();
  const contactReveal = useReveal();

  // ─── Brand-themed visuals ────────────────
  const isDon = brand.id === 'donvito';

  // Items belonging to the currently-active brand (brand === current or 'both')
  const brandMatches = (b?: string) => b === brand.id || b === 'both' || !b;

  // Featured items — pick popular ones from this brand only
  const featured = items
    .filter((i) => i.is_available && !i.sold_out && i.image_url && brandMatches(i.brand))
    .sort((a, b) => (b.is_popular ? 1 : 0) - (a.is_popular ? 1 : 0))
    .slice(0, 6);

  // For each category, find its image or the first matching menu-item image.
  // No Unsplash fallbacks — a category without a real DB image is dropped below.
  const availableItemImages = items
    .filter((i) => i.image_url && i.is_available && !i.sold_out && brandMatches(i.brand))
    .map((i) => ({ category: i.category, url: i.image_url! }));
  // Filter categories to this brand only.
  // For Don Vito: strict pizza+pasta match (drop Antipasti/Dolci/Insalat/Beverages etc.)
  // For SushiKi: anything that maps to sushiki or 'both'.
  const donvitoStrict = /(pizza|pizzen|pasta|spaghetti|penne|lasagn|ravioli|gnocchi|tagliatell|fettuccin|linguin|rigatoni|tortellini|risott)/i;
  const brandCategories = categories.filter((c) => {
    if (isDon) return donvitoStrict.test(c.name);
    const cb = deriveBrandFromCategory(c.name);
    return cb === brand.id || cb === 'both';
  });
  // Build tiles. For Don Vito: build 4 tiles = 2 Pizza + 2 Pasta, each with a different item image.
  // For other brands: dedupe categories by name and pick first unused image.
  const usedImgs = new Set<string>();
  let featuredCategories: Category[] = [];

  if (isDon) {
    const pickImages = (re: RegExp, count: number): string[] => {
      const pool = availableItemImages.filter((i) => re.test(i.category));
      const picks: string[] = [];
      for (const p of pool) {
        if (picks.length >= count) break;
        if (usedImgs.has(p.url) || picks.includes(p.url)) continue;
        picks.push(p.url);
      }
      return picks;
    };
    const pizzaImgs = pickImages(/pizza/i, 2);
    const pastaImgs = pickImages(/pasta|spaghetti|penne|lasagn|ravioli|gnocchi|tagliatell|fettuccin|linguin|rigatoni|tortellini|risott|maccheroni/i, 2);
    const pizzaLabel = langKey === 'de' ? 'Pizza' : 'Pizza';
    const pastaLabel = langKey === 'de' ? 'Pasta' : 'Pasta';
    const tiles: Category[] = [];
    pizzaImgs.forEach((url, idx) => {
      tiles.push({ id: `pizza-${idx}`, name: pizzaLabel, icon: '', image_url: url, is_active: true, display_order: idx });
      usedImgs.add(url);
    });
    pastaImgs.forEach((url, idx) => {
      tiles.push({ id: `pasta-${idx}`, name: pastaLabel, icon: '', image_url: url, is_active: true, display_order: 10 + idx });
      usedImgs.add(url);
    });
    // Interleave Pizza, Pasta, Pizza, Pasta for visual variety
    const interleaved: Category[] = [];
    const pizzas = tiles.filter((t) => t.id.startsWith('pizza-'));
    const pastas = tiles.filter((t) => t.id.startsWith('pasta-'));
    for (let i = 0; i < Math.max(pizzas.length, pastas.length); i++) {
      if (pizzas[i]) interleaved.push(pizzas[i]);
      if (pastas[i]) interleaved.push(pastas[i]);
    }
    featuredCategories = interleaved.slice(0, 4);
  } else {
    const seenNames = new Set<string>();
    const uniqueBrandCategories = brandCategories.filter((c) => {
      const key = (c.name || '').trim().toLowerCase();
      if (!key || seenNames.has(key)) return false;
      seenNames.add(key);
      return true;
    });
    featuredCategories = uniqueBrandCategories
      .map((c) => {
        const matches = availableItemImages.filter((i) => i.category === c.name);
        let img = c.image_url && !usedImgs.has(c.image_url) ? c.image_url : '';
        if (!img) img = matches.find((m) => !usedImgs.has(m.url))?.url || '';
        if (!img) return null;
        usedImgs.add(img);
        return { ...c, image_url: img };
      })
      .filter((c): c is Category => c !== null)
      .slice(0, 4);
  }

  // Build inline CSS variables for current brand
  const brandVars = {
    '--b-primary': brand.primary,
    '--b-primary-dark': brand.primaryDark,
    '--b-accent': brand.accent,
    '--b-bg': brand.bg,
    '--b-bg-elev': brand.bgElev,
    '--b-text': brand.text,
    '--b-muted': brand.textMuted,
    '--b-font-display': brand.fontDisplay,
    '--b-font-body': brand.fontBody,
    backgroundColor: brand.bg,
    color: brand.text,
    fontFamily: brand.fontBody,
  } as React.CSSProperties;

  return (
    <div style={brandVars}>
      {/* Fonts */}
      <link
        rel="stylesheet"
        href={
          isDon
            ? 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Inter:wght@400;500;600;700&display=swap'
            : 'https://fonts.googleapis.com/css2?family=Inter+Tight:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap'
        }
      />

      {/* Global keyframes + vars */}
      <style>{`
        @keyframes brandFadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes brandFadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes brandZoomOut { from { transform: scale(1.08); } to { transform: scale(1); } }
        @keyframes brandFloat   { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes brandPulse   { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
        @keyframes brandScrollHint { 0% { transform: translateY(0); opacity: 0; } 30% { opacity: 1; } 100% { transform: translateY(10px); opacity: 0; } }
        @keyframes brandShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes brandGradientShift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }

        .lp-reveal { opacity: 1; transform: none; transition: opacity .6s ease, transform .6s cubic-bezier(.22,1,.36,1); }
        .lp-reveal.visible { opacity: 1; transform: none; }

        /* Tighten section padding on mobile so sections stack without big black voids */
        @media (max-width: 768px) {
          .lp-section { padding-top: 48px !important; padding-bottom: 48px !important; }
          .lp-section-heading { margin-bottom: 28px !important; }
        }

        .lp-hero-word { display: inline-block; opacity: 0; animation: brandFadeUp .8s cubic-bezier(.22,1,.36,1) forwards; }

        .lp-card-hover { transition: transform .5s cubic-bezier(.22,1,.36,1), box-shadow .5s; }
        .lp-card-hover:hover { transform: translateY(-6px); }

        .lp-tilt { transition: transform .6s cubic-bezier(.22,1,.36,1); }
        .lp-tilt:hover { transform: perspective(800px) rotateX(4deg) rotateY(-4deg) translateY(-8px); }

        .lp-skeleton { position: relative; }
        .lp-skeleton::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.06) 50%, transparent 100%);
          background-size: 200% 100%;
          animation: brandShimmer 1.6s linear infinite;
          pointer-events: none;
        }

        .lp-btn-primary {
          position: relative; overflow: hidden;
          transition: transform .3s ease, box-shadow .3s ease;
        }
        .lp-btn-primary::after {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,.4) 50%, transparent 70%);
          background-size: 200% 100%;
          opacity: 0; transition: opacity .3s;
        }
        .lp-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 16px 40px rgba(0,0,0,.45); }
        .lp-btn-primary:hover::after { opacity: 1; animation: brandShimmer 1.4s ease-in-out infinite; }

        .lp-link-underline { position: relative; }
        .lp-link-underline::after {
          content: ''; position: absolute; left: 0; bottom: -2px; height: 1px; width: 100%;
          background: currentColor; transform: scaleX(0); transform-origin: right;
          transition: transform .5s cubic-bezier(.22,1,.36,1);
        }
        .lp-link-underline:hover::after { transform: scaleX(1); transform-origin: left; }

        @media (prefers-reduced-motion: reduce) {
          .lp-reveal, .lp-hero-word { transition: none !important; animation: none !important; opacity: 1 !important; transform: none !important; }
        }

        .lp-scroll-snap { scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; }
        .lp-scroll-snap > * { scroll-snap-align: start; }

        .lp-marquee { display: flex; gap: 32px; animation: lp-marquee 40s linear infinite; will-change: transform; }
        @keyframes lp-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }

        .lp-hero-bg::before {
          content: ''; position: absolute; inset: 0; z-index: 1;
          background: ${isDon
            ? `radial-gradient(ellipse at 30% 20%, rgba(232,169,75,.28), transparent 60%),
               radial-gradient(ellipse at 70% 80%, rgba(245,234,209,.08), transparent 60%),
               linear-gradient(180deg, rgba(10,8,7,.72) 0%, rgba(10,8,7,.96) 100%)`
            : `radial-gradient(ellipse at 20% 30%, rgba(148,232,21,.18), transparent 55%),
               radial-gradient(ellipse at 80% 70%, rgba(148,232,21,.1), transparent 55%),
               linear-gradient(180deg, rgba(10,10,10,.55) 0%, rgba(10,10,10,.95) 100%)`};
        }

        .lp-noise {
          position: absolute; inset: 0; z-index: 2; pointer-events: none; opacity: .035; mix-blend-mode: overlay;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
        }
      `}</style>

      {/* ═══════════════════════════════════════════════
          HEADER (sticky, translucent)
         ═══════════════════════════════════════════════ */}
      <header
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          zIndex: 100,
          padding: scrolled ? '10px 24px' : '20px 24px',
          background: scrolled ? `${brand.bg}ee` : 'transparent',
          backdropFilter: scrolled ? 'blur(20px) saturate(160%)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(160%)' : 'none',
          borderBottom: scrolled ? `1px solid ${brand.text}10` : '1px solid transparent',
          transition: 'all .4s cubic-bezier(.22,1,.36,1)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        {/* Brand logo / name */}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            cursor: 'pointer',
          }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <img
            src={brand.id === 'donvito' ? '/donvito_logo.svg?v=2' : brand.logo}
            alt={brand.name}
            style={{
              height: scrolled ? 84 : 128,
              transition: 'height .35s cubic-bezier(.22,1,.36,1)',
              objectFit: 'contain',
              filter: `drop-shadow(0 2px 10px ${brand.primary}45)`,
            }}
          />
        </div>

        {/* Right side actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Lang toggle */}
          <div
            style={{
              display: 'flex',
              background: `${brand.text}0d`,
              borderRadius: 999,
              padding: 3,
              border: `1px solid ${brand.text}15`,
            }}
          >
            {(['de', 'en'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                style={{
                  background: lang === l ? brand.primary : 'transparent',
                  color: lang === l ? '#0a0a0a' : brand.textMuted,
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: 999,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all .25s',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {l}
              </button>
            ))}
          </div>

          {/* Auth */}
          {user ? (
            <button
              onClick={() => navigate('/account')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: `${brand.text}0d`,
                border: `1px solid ${brand.text}20`,
                color: brand.text,
                padding: '7px 14px 7px 7px',
                borderRadius: 999,
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all .25s',
              }}
            >
              <span
                style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: brand.primary, color: '#0a0a0a',
                  display: 'grid', placeItems: 'center',
                  fontSize: '0.75rem', fontWeight: 800,
                }}
              >
                {(profile?.name || user.email || '?')[0].toUpperCase()}
              </span>
              <span className="desktop-only" style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {profile?.name || user.email?.split('@')[0]}
              </span>
            </button>
          ) : (
            <button
              onClick={() => navigate('/auth')}
              style={{
                background: 'transparent',
                border: `1px solid ${brand.text}30`,
                color: brand.text,
                padding: '8px 16px',
                borderRadius: 999,
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all .25s',
              }}
            >
              {langKey === 'de' ? 'Anmelden' : 'Sign in'}
            </button>
          )}

          {/* Order CTA — sticky visible only when scrolled */}
          <button
            onClick={() => goToMenu('delivery')}
            className="lp-btn-primary"
            style={{
              background: brand.primary,
              color: '#0a0a0a',
              border: 'none',
              padding: '10px 20px',
              borderRadius: 999,
              fontSize: '0.85rem',
              fontWeight: 700,
              cursor: 'pointer',
              opacity: scrolled ? 1 : 0,
              transform: scrolled ? 'translateX(0)' : 'translateX(20px)',
              pointerEvents: scrolled ? 'auto' : 'none',
              transition: 'all .35s cubic-bezier(.22,1,.36,1)',
              letterSpacing: '0.02em',
            }}
          >
            {langKey === 'de' ? 'Bestellen' : 'Order'}
          </button>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════
          HERO — fullscreen
         ═══════════════════════════════════════════════ */}
      <section
        className="lp-hero-bg"
        style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          textAlign: 'center',
          padding: '120px 24px 80px',
          overflow: 'hidden',
          background: isDon
            ? 'linear-gradient(135deg, #0f0c09 0%, #0a0807 50%, #1a1510 100%)'
            : 'linear-gradient(135deg, #0a0a0a 0%, #141414 50%, #0a0a0a 100%)',
          backgroundSize: '200% 200%',
          animation: 'brandGradientShift 18s ease infinite',
        }}
      >
        <div className="lp-noise" />

        {/* Open/Closed pill */}
        <div
          style={{
            position: 'relative', zIndex: 3,
            marginBottom: 24,
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: `${brand.text}0a`,
            border: `1px solid ${brand.text}20`,
            padding: '8px 16px',
            borderRadius: 999,
            fontSize: '0.78rem',
            color: brand.textMuted,
            backdropFilter: 'blur(10px)',
            animation: 'brandFadeIn 1s ease .1s both',
          }}
        >
          <span
            style={{
              width: 8, height: 8, borderRadius: '50%',
              background: openState.open ? '#4CAF50' : '#ef5350',
              animation: openState.open ? 'brandPulse 2s infinite' : 'none',
              boxShadow: openState.open ? '0 0 8px #4CAF50' : 'none',
            }}
          />
          <span style={{ fontWeight: 600 }}>{openState.label[langKey]}</span>
          <span style={{ opacity: 0.5 }}>·</span>
          <span>{isDon ? 'Hürth' : contact.label}</span>
        </div>

        {/* Kicker */}
        <p
          style={{
            position: 'relative', zIndex: 3,
            color: brand.primary,
            fontSize: '0.82rem', fontWeight: 600,
            letterSpacing: '0.3em', textTransform: 'uppercase',
            margin: '0 0 18px',
            animation: 'brandFadeUp .9s cubic-bezier(.22,1,.36,1) .2s both',
          }}
        >
          {localCity(brand.heroKicker[langKey])}
        </p>

        {/* Display title — word-by-word reveal */}
        <h1
          style={{
            position: 'relative', zIndex: 3,
            fontFamily: brand.fontDisplay,
            fontSize: 'clamp(2.4rem, 8vw, 5.5rem)',
            lineHeight: 1.05,
            fontWeight: isDon ? 700 : 800,
            letterSpacing: isDon ? '-0.01em' : '-0.03em',
            color: brand.text,
            margin: '0 0 20px',
            maxWidth: 900,
            fontStyle: 'normal',
          }}
        >
          {brand.tagline[langKey].split(' ').map((word, i) => (
            <span
              key={i}
              className="lp-hero-word"
              style={{
                animationDelay: `${0.35 + i * 0.15}s`,
                marginRight: '0.25em',
              }}
            >
              {word}
            </span>
          ))}
        </h1>

        {/* Rotating sub-tagline */}
        <div
          style={{
            position: 'relative', zIndex: 3,
            height: 32, marginBottom: 40,
            overflow: 'hidden',
            animation: 'brandFadeIn .8s ease 1.1s both',
          }}
        >
          {brand.subTagline[langKey].map((line, i) => (
            <p
              key={i}
              style={{
                position: 'absolute',
                left: '50%',
                top: 0,
                transform: `translateX(-50%) translateY(${(i - sublineIdx) * 32}px)`,
                color: brand.textMuted,
                fontSize: 'clamp(1rem, 2.4vw, 1.25rem)',
                margin: 0,
                whiteSpace: 'nowrap',
                fontWeight: 400,
                transition: 'transform .6s cubic-bezier(.22,1,.36,1), opacity .4s',
                opacity: i === sublineIdx ? 1 : 0,
              }}
            >
              {localCity(line)}
            </p>
          ))}
        </div>

        {/* Branch selector — SushiKi only (Don Vito is Hürth-only) */}
        {!isDon && (
          <div
            style={{
              position: 'relative', zIndex: 3,
              display: 'inline-flex', gap: 6, marginBottom: 28,
              background: `${brand.text}0a`,
              border: `1px solid ${brand.text}18`,
              borderRadius: 999,
              padding: '5px 6px',
              animation: 'brandFadeIn .8s ease 1.1s both',
            }}
          >
            {shopBranches.map((b) => (
              <button
                key={b}
                onClick={() => selectBranch(b)}
                style={{
                  background: branch === b ? brand.primary : 'transparent',
                  color: branch === b ? '#0a0a0a' : brand.textMuted,
                  border: 'none',
                  padding: '7px 20px',
                  borderRadius: 999,
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all .22s',
                  letterSpacing: '0.02em',
                }}
              >
                {branchContact(b).label}
              </button>
            ))}
          </div>
        )}

        {/* CTAs */}
        <div
          style={{
            position: 'relative', zIndex: 3,
            display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center',
            animation: 'brandFadeUp .9s cubic-bezier(.22,1,.36,1) 1.2s both',
          }}
        >
          <button
            onClick={() => goToMenu('delivery')}
            className="lp-btn-primary"
            style={{
              background: brand.primary,
              color: '#0a0a0a',
              border: 'none',
              padding: '18px 38px',
              borderRadius: 999,
              fontSize: '1rem',
              fontWeight: 800,
              cursor: 'pointer',
              letterSpacing: '0.02em',
              boxShadow: `0 14px 40px ${brand.primary}45`,
              display: 'inline-flex', alignItems: 'center', gap: 10,
            }}
          >
            <span>🛵</span>
            <span>{langKey === 'de' ? 'Jetzt liefern lassen' : 'Order delivery'}</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span style={{ opacity: 0.75, fontWeight: 600 }}>{deliveryTime} min</span>
          </button>

          {/* Pickup — equal visual weight with delivery (cream fill, same size,
           * same shadow). Client owner asked that pickup be "presented equally"
           * since pickup is more profitable. */}
          <button
            onClick={() => goToMenu('pickup')}
            style={{
              background: brand.text,
              color: '#0a0a0a',
              border: 'none',
              padding: '18px 38px',
              borderRadius: 999,
              fontSize: '1rem',
              fontWeight: 800,
              cursor: 'pointer',
              letterSpacing: '0.02em',
              boxShadow: `0 14px 40px ${brand.text}35`,
              display: 'inline-flex', alignItems: 'center', gap: 10,
              transition: 'all .3s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = `0 18px 48px ${brand.text}45`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 14px 40px ${brand.text}35`;
            }}
          >
            <span>🥡</span>
            <span>{langKey === 'de' ? 'Selbst abholen' : 'Pickup'}</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span style={{ opacity: 0.75, fontWeight: 600 }}>~15 min</span>
          </button>
        </div>

        {/* Cross-promo subtle pill */}
        <button
          onClick={() => switchTo(other.id)}
          style={{
            position: 'relative', zIndex: 3,
            marginTop: 32,
            background: 'transparent',
            border: 'none',
            color: brand.textMuted,
            fontSize: '0.88rem',
            cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 16px',
            borderRadius: 999,
            transition: 'all .3s',
            animation: 'brandFadeIn 1s ease 1.5s both',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = brand.text;
            e.currentTarget.style.background = `${brand.text}08`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = brand.textMuted;
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <span>
            {langKey === 'de'
              ? `Auch Lust auf ${other.id === 'donvito' ? 'Pizza' : 'Sushi'}?`
              : `Also craving ${other.id === 'donvito' ? 'pizza' : 'sushi'}?`}
          </span>
          <span style={{ color: brand.primary, fontWeight: 700 }}>→</span>
        </button>

        {/* Scroll hint */}
        <div
          style={{
            position: 'absolute',
            bottom: 32, left: '50%', transform: 'translateX(-50%)',
            zIndex: 3,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            color: brand.textMuted,
            fontSize: '0.68rem',
            letterSpacing: '0.3em', textTransform: 'uppercase',
            opacity: 0.6,
            pointerEvents: 'none',
          }}
        >
          <span>Scroll</span>
          <div
            style={{
              width: 1, height: 40,
              background: `linear-gradient(180deg, transparent 0%, ${brand.text} 50%, transparent 100%)`,
              position: 'relative', overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute', left: 0, right: 0, top: 0,
                width: '100%', height: 10,
                background: brand.primary,
                animation: 'brandScrollHint 2.2s ease-in-out infinite',
              }}
            />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          USP STRIP — marquee of value props
         ═══════════════════════════════════════════════ */}
      <section
        ref={uspReveal.ref}
        className={`lp-reveal lp-section ${uspReveal.visible ? 'visible' : ''}`}
        style={{
          position: 'relative',
          padding: '48px 0',
          background: brand.bgElev,
          borderTop: `1px solid ${brand.text}08`,
          borderBottom: `1px solid ${brand.text}08`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            maxWidth: 1200, margin: '0 auto', padding: '0 24px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 24,
          }}
        >
          {[
            {
              icon: '⚡',
              num: `${deliveryTime}`,
              unit: 'min',
              label: langKey === 'de' ? 'Schnelle Lieferung' : 'Fast delivery',
              sub: langKey === 'de' ? 'Ab Bestellbestätigung' : 'From order confirmation',
            },
            {
              icon: '★',
              num: '4.8',
              unit: '/5',
              label: langKey === 'de' ? 'Kundenbewertung' : 'Customer rating',
              sub: langKey === 'de' ? 'Über 500 Bewertungen' : 'Over 500 reviews',
            },
            {
              icon: '🌿',
              num: '100',
              unit: '%',
              label: langKey === 'de' ? 'Frisch täglich' : 'Fresh daily',
              sub: langKey === 'de' ? 'Keine Tiefkühlware' : 'Never frozen',
            },
            {
              icon: '🎁',
              num: 'Ab €25',
              unit: '',
              label: langKey === 'de' ? 'Gratis Lieferung' : 'Free delivery',
              sub: langKey === 'de' ? 'In Hürth & Umgebung' : 'In Hürth & around',
            },
          ].map((u, i) => (
            <div
              key={i}
              style={{
                textAlign: 'center',
                padding: '24px 16px',
                borderRadius: 16,
                transition: 'all .35s',
              }}
            >
              <div
                style={{
                  fontSize: '2rem',
                  marginBottom: 10,
                  color: brand.primary,
                }}
              >
                {u.icon}
              </div>
              <div
                style={{
                  fontFamily: brand.fontDisplay,
                  fontSize: '2.2rem',
                  fontWeight: 800,
                  color: brand.text,
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                }}
              >
                {u.num}
                <span style={{ color: brand.textMuted, fontWeight: 400, fontSize: '0.55em', marginLeft: 2 }}>
                  {u.unit}
                </span>
              </div>
              <div
                style={{
                  marginTop: 8,
                  color: brand.text,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                }}
              >
                {u.label}
              </div>
              <div
                style={{
                  color: brand.textMuted,
                  fontSize: '0.78rem',
                  marginTop: 4,
                }}
              >
                {u.sub}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          FEATURED CATEGORIES
         ═══════════════════════════════════════════════ */}
      <section
        ref={categoriesReveal.ref}
        className={`lp-reveal lp-section ${categoriesReveal.visible ? 'visible' : ''}`}
        style={{ padding: '64px 24px 48px', maxWidth: 1400, margin: '0 auto' }}
      >
        <div className="lp-section-heading" style={{ textAlign: 'center', marginBottom: 40 }}>
          <p
            style={{
              color: brand.primary,
              fontSize: '0.78rem', fontWeight: 700,
              letterSpacing: '0.3em', textTransform: 'uppercase',
              margin: '0 0 12px',
            }}
          >
            {langKey === 'de' ? 'Unsere Karte' : 'Our Menu'}
          </p>
          <h2
            style={{
              fontFamily: brand.fontDisplay,
              fontSize: 'clamp(1.8rem, 4.5vw, 3rem)',
              fontWeight: 700,
              color: brand.text,
              margin: 0,
              letterSpacing: '-0.02em',
              fontStyle: 'normal',
            }}
          >
            {langKey === 'de' ? 'Was dich erwartet' : 'What awaits you'}
          </h2>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16,
          }}
        >
          {(featuredCategories.length > 0
            ? featuredCategories
            : (dataLoaded
              ? []
              : ([0, 1, 2, 3].map((i) => ({ id: `skeleton-${i}`, name: '', icon: '', image_url: '', is_active: true, display_order: i })) as Category[])
            )
          ).map((c, idx) => {
            const isSkeleton = typeof c.id === 'string' && c.id.startsWith('skeleton-');
            const isAboveFold = idx < 2;
            return (
            <button
              key={c.id}
              onClick={isSkeleton ? undefined : () => goToMenu('delivery')}
              className={isSkeleton ? 'lp-skeleton' : 'lp-tilt'}
              disabled={isSkeleton}
              style={{
                border: 'none',
                borderRadius: 20,
                overflow: 'hidden',
                position: 'relative',
                height: 220,
                cursor: isSkeleton ? 'default' : 'pointer',
                background: `linear-gradient(135deg, ${brand.primary}18 0%, ${brand.bgElev} 100%)`,
                textAlign: 'left',
                padding: 0,
              }}
            >
              {c.image_url && (
                <>
                  <img
                    src={c.image_url}
                    alt=""
                    loading={isAboveFold ? 'eager' : 'lazy'}
                    decoding="async"
                    {...(isAboveFold ? { fetchpriority: 'high' as any } : {})}
                    onLoad={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '1'; }}
                    style={{
                      position: 'absolute', inset: 0,
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                      opacity: 0,
                      transition: 'opacity .4s ease, transform .6s',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(180deg, rgba(0,0,0,.1) 0%, rgba(0,0,0,.75) 100%)',
                      pointerEvents: 'none',
                    }}
                  />
                </>
              )}
              {!isSkeleton && (
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', flexDirection: 'column',
                    justifyContent: 'flex-end',
                    padding: 20,
                    color: brand.text,
                  }}
                >
                  <div style={{ marginBottom: 8, display: 'flex' }}>
                    {isDon ? (
                      <img
                        src="/donvito_mark.svg?v=2"
                        alt=""
                        style={{ height: 44, width: 'auto', objectFit: 'contain' }}
                      />
                    ) : (
                      <SushiMarkIcon size={44} />
                    )}
                  </div>
                  <div
                    style={{
                      fontFamily: brand.fontDisplay,
                      fontSize: '1.3rem',
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {c.name}
                  </div>
                  <div
                    style={{
                      fontSize: '0.82rem',
                      color: brand.textMuted,
                      marginTop: 4,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <span>{langKey === 'de' ? 'Entdecken' : 'Explore'}</span>
                    <span style={{ color: brand.primary }}>→</span>
                  </div>
                </div>
              )}
            </button>
            );
          })}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          FEATURED FOOD GRID — Instagram-style
         ═══════════════════════════════════════════════ */}
      {featured.length > 0 && (
        <section
          ref={gridReveal.ref}
          className={`lp-reveal lp-section ${gridReveal.visible ? 'visible' : ''}`}
          style={{ padding: '32px 24px 64px', maxWidth: 1400, margin: '0 auto' }}
        >
          <div className="lp-section-heading" style={{ textAlign: 'center', marginBottom: 32 }}>
            <p
              style={{
                color: brand.primary,
                fontSize: '0.78rem', fontWeight: 700,
                letterSpacing: '0.3em', textTransform: 'uppercase',
                margin: '0 0 12px',
              }}
            >
              {langKey === 'de' ? 'Bestseller' : 'Bestsellers'}
            </p>
            <h2
              style={{
                fontFamily: brand.fontDisplay,
                fontSize: 'clamp(1.6rem, 4vw, 2.6rem)',
                fontWeight: 700,
                color: brand.text,
                margin: 0,
                letterSpacing: '-0.02em',
                fontStyle: 'normal',
              }}
            >
              {langKey === 'de' ? 'Beliebt bei unseren Gästen' : 'Loved by our guests'}
            </h2>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 12,
            }}
          >
            {featured.map((item) => (
              <div
                key={item.id}
                className="lp-card-hover"
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 16,
                  overflow: 'hidden',
                  background: brand.bgElev,
                  border: `1px solid ${brand.text}1f`,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  addItem({
                    menuItemId: item.id,
                    name: item.name,
                    name_de: item.name_de,
                    price: item.price_with_tax || item.base_price,
                    quantity: 1,
                    categoryName: item.category,
                    imageUrl: item.image_url,
                    notes: '',
                    addons: [],
                  });
                  goToMenu('delivery');
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '1/1',
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, ${brand.primary}14 0%, ${brand.bgElev} 100%)`,
                  }}
                >
                  <img
                    src={item.image_url}
                    alt={langKey === 'de' ? item.name_de : item.name}
                    loading="lazy"
                    decoding="async"
                    onLoad={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '1'; }}
                    style={{
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                      opacity: 0,
                      transition: 'opacity .35s ease, transform .8s cubic-bezier(.22,1,.36,1)',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  />
                  {item.is_popular && (
                    <div
                      style={{
                        position: 'absolute', top: 10, left: 10,
                        background: brand.primary, color: '#0a0a0a',
                        fontSize: '0.65rem', fontWeight: 800,
                        padding: '4px 10px',
                        borderRadius: 999,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      ★ {langKey === 'de' ? 'Beliebt' : 'Popular'}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    padding: '12px 14px 14px',
                    borderTop: `1px solid ${brand.text}14`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    minHeight: 78,
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.92rem', fontWeight: 700,
                      color: brand.text,
                      letterSpacing: '-0.005em',
                      lineHeight: 1.25,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {langKey === 'de' ? item.name_de || item.name : item.name}
                  </div>
                  <div
                    style={{
                      fontSize: '0.9rem',
                      color: brand.primary,
                      fontWeight: 700,
                      marginTop: 'auto',
                    }}
                  >
                    €{(item.price_with_tax || item.base_price).toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════
          BRAND STORY
         ═══════════════════════════════════════════════ */}
      <section
        ref={storyReveal.ref}
        className={`lp-reveal lp-section ${storyReveal.visible ? 'visible' : ''}`}
        style={{
          padding: '72px 24px',
          background: brand.bgElev,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            maxWidth: 1100, margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 60,
            alignItems: 'center',
          }}
        >
          <div style={{ position: 'relative' }}>
            <div
              style={{
                aspectRatio: '4/5',
                borderRadius: 24,
                background: isDon
                  ? `linear-gradient(180deg, rgba(0,0,0,.1) 0%, rgba(0,0,0,.75) 100%), url(https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1600&q=85) center/cover`
                  : `linear-gradient(180deg, rgba(0,0,0,.1) 0%, rgba(0,0,0,.75) 100%), url(https://images.unsplash.com/photo-1553621042-f6e147245754?auto=format&fit=crop&w=1600&q=85) center/cover`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute', left: 20, bottom: 20,
                  color: brand.text,
                  fontSize: '0.75rem',
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  opacity: 0.6,
                }}
              >
                {brand.name} · Est. {isDon ? 2008 : 2022}
              </div>
            </div>
          </div>
          <div>
            <p
              style={{
                color: brand.primary,
                fontSize: '0.78rem', fontWeight: 700,
                letterSpacing: '0.3em', textTransform: 'uppercase',
                margin: '0 0 14px',
              }}
            >
              {langKey === 'de' ? 'Über uns' : 'About us'}
            </p>
            <h2
              style={{
                fontFamily: brand.fontDisplay,
                fontSize: 'clamp(1.8rem, 4.5vw, 2.8rem)',
                fontWeight: 700,
                color: brand.text,
                margin: '0 0 24px',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                fontStyle: 'normal',
              }}
            >
              {brand.storyTitle[langKey]}
            </h2>
            <p
              style={{
                color: brand.textMuted,
                fontSize: '1.02rem',
                lineHeight: 1.7,
                margin: '0 0 28px',
              }}
            >
              {brand.storyBody[langKey]}
            </p>
            <blockquote
              style={{
                fontFamily: brand.fontDisplay,
                fontSize: '1.15rem',
                fontStyle: 'italic',
                color: brand.text,
                margin: 0,
                paddingLeft: 18,
                borderLeft: `3px solid ${brand.primary}`,
                lineHeight: 1.5,
              }}
            >
              {brand.storyQuote[langKey]}
            </blockquote>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          REVIEWS
         ═══════════════════════════════════════════════ */}
      <section
        ref={reviewsReveal.ref}
        className={`lp-reveal lp-section ${reviewsReveal.visible ? 'visible' : ''}`}
        style={{ padding: '72px 24px', maxWidth: 1200, margin: '0 auto' }}
      >
        <div className="lp-section-heading" style={{ textAlign: 'center', marginBottom: 40 }}>
          <p
            style={{
              color: brand.primary,
              fontSize: '0.78rem', fontWeight: 700,
              letterSpacing: '0.3em', textTransform: 'uppercase',
              margin: '0 0 12px',
            }}
          >
            {langKey === 'de' ? 'Stimmen unserer Gäste' : 'What guests say'}
          </p>
          <h2
            style={{
              fontFamily: brand.fontDisplay,
              fontSize: 'clamp(1.6rem, 4vw, 2.6rem)',
              fontWeight: 700,
              color: brand.text,
              margin: 0,
              letterSpacing: '-0.02em',
              fontStyle: 'normal',
            }}
          >
            ★★★★★ <span style={{ color: brand.textMuted, fontWeight: 400, fontSize: '0.7em' }}>4.8 / 5</span>
          </h2>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 20,
          }}
        >
          {(isDon
            ? [
                { name: 'Marco S.', text: langKey === 'de' ? 'Beste Pizza in Hürth. Teig ist perfekt, Mozzarella echt italienisch. Fühlt sich an wie Urlaub.' : 'Best pizza in Hürth. Dough is perfect, mozzarella truly Italian. Feels like a vacation.', stars: 5 },
                { name: 'Anna K.', text: langKey === 'de' ? 'Super schnelle Lieferung und noch ofenwarm. Die Pizza Diavola ist der Hammer!' : 'Super fast delivery, still oven-warm. The Pizza Diavola is amazing!', stars: 5 },
                { name: 'Tim R.', text: langKey === 'de' ? 'Endlich echte italienische Pizza. Keine Fast-Food-Qualität. Klare Empfehlung.' : 'Finally real Italian pizza. No fast-food quality. Highly recommended.', stars: 5 },
              ]
            : [
                { name: 'Sarah M.', text: langKey === 'de' ? 'Bestes Sushi in Hürth. Frisch, schön angerichtet und schnell geliefert. Immer wieder gern!' : 'Best sushi in Hürth. Fresh, beautifully plated, and delivered fast. Will order again!', stars: 5 },
                { name: 'Lukas H.', text: langKey === 'de' ? 'Die Maki-Platten sind unschlagbar. Kleine Details machen hier den Unterschied.' : 'The maki platters are unbeatable. Small details make all the difference here.', stars: 5 },
                { name: 'Priya P.', text: langKey === 'de' ? 'Modernes Ambiente, freundliches Team, frische Zutaten. Absolut empfehlenswert.' : 'Modern atmosphere, friendly team, fresh ingredients. Absolutely recommend.', stars: 5 },
              ]
          ).map((r, i) => (
            <div
              key={i}
              className="lp-card-hover"
              style={{
                padding: 28,
                borderRadius: 20,
                background: brand.bgElev,
                border: `1px solid ${brand.text}10`,
              }}
            >
              <div
                style={{
                  color: brand.primary,
                  fontSize: '0.9rem',
                  marginBottom: 12,
                  letterSpacing: '0.2em',
                }}
              >
                {'★'.repeat(r.stars)}
              </div>
              <p
                style={{
                  color: brand.text,
                  fontSize: '0.98rem',
                  lineHeight: 1.6,
                  margin: '0 0 18px',
                  fontStyle: 'normal',
                }}
              >
                „{r.text}"
              </p>
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
              >
                <div
                  style={{
                    width: 34, height: 34,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.primaryDark} 100%)`,
                    color: '#0a0a0a',
                    display: 'grid', placeItems: 'center',
                    fontWeight: 800,
                    fontSize: '0.82rem',
                  }}
                >
                  {r.name[0]}
                </div>
                <div>
                  <div style={{ color: brand.text, fontSize: '0.88rem', fontWeight: 600 }}>{r.name}</div>
                  <div style={{ color: brand.textMuted, fontSize: '0.72rem' }}>Google Review</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          CROSS-PROMO BANNER
         ═══════════════════════════════════════════════ */}
      <section
        ref={promoReveal.ref}
        className={`lp-reveal lp-section ${promoReveal.visible ? 'visible' : ''}`}
        style={{
          padding: '48px 24px',
          background: `linear-gradient(135deg, ${other.bg} 0%, ${other.bgElev} 100%)`,
          borderTop: `1px solid ${other.primary}25`,
          borderBottom: `1px solid ${other.primary}25`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute', top: '-40%', right: '-10%',
            width: 500, height: 500,
            background: `radial-gradient(circle, ${other.primary}15 0%, transparent 70%)`,
            borderRadius: '50%',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            maxWidth: 1100, margin: '0 auto',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 40, flexWrap: 'wrap',
            position: 'relative', zIndex: 1,
          }}
        >
          <div style={{ flex: '1 1 400px' }}>
            <p
              style={{
                color: other.primary,
                fontSize: '0.78rem', fontWeight: 700,
                letterSpacing: '0.3em', textTransform: 'uppercase',
                margin: '0 0 12px',
              }}
            >
              {langKey === 'de' ? 'Exklusiv bei uns' : 'Exclusive with us'}
            </p>
            <h2
              style={{
                fontFamily: other.fontDisplay,
                fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)',
                fontWeight: 700,
                color: other.text,
                margin: '0 0 12px',
                letterSpacing: '-0.02em',
                fontStyle: 'normal',
              }}
            >
              {brand.crossPromoTitle[langKey]}
            </h2>
            <p
              style={{
                color: other.textMuted,
                fontSize: '1rem',
                lineHeight: 1.6,
                margin: 0,
                maxWidth: 480,
              }}
            >
              {brand.crossPromoText[langKey]}
            </p>
          </div>
          <button
            onClick={() => switchTo(other.id)}
            className="lp-btn-primary"
            style={{
              background: other.primary,
              color: '#0a0807',
              border: 'none',
              padding: '18px 32px',
              borderRadius: 999,
              fontSize: '1rem',
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.02em',
              display: 'inline-flex', alignItems: 'center', gap: 10,
              boxShadow: `0 14px 40px ${other.primary}40`,
              whiteSpace: 'nowrap',
            }}
          >
            <img
              src={other.id === 'donvito' ? '/donvito_mark_hd.png?v=4' : '/sushiki_mark_black.png?v=4'}
              alt=""
              loading="eager"
              decoding="sync"
              style={{ height: 28, width: 28, objectFit: 'contain' }}
            />
            <span>{brand.crossPromoCta[langKey]}</span>
            <span>→</span>
          </button>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          CONTACT + MAP
         ═══════════════════════════════════════════════ */}
      <section
        ref={contactReveal.ref}
        className={`lp-reveal lp-section ${contactReveal.visible ? 'visible' : ''}`}
        style={{ padding: '72px 24px', maxWidth: 1200, margin: '0 auto' }}
      >
        <div className="lp-section-heading" style={{ textAlign: 'center', marginBottom: 40 }}>
          <p
            style={{
              color: brand.primary,
              fontSize: '0.78rem', fontWeight: 700,
              letterSpacing: '0.3em', textTransform: 'uppercase',
              margin: '0 0 12px',
            }}
          >
            {langKey === 'de' ? 'Besuche uns' : 'Visit us'}
          </p>
          <h2
            style={{
              fontFamily: brand.fontDisplay,
              fontSize: 'clamp(1.6rem, 4vw, 2.6rem)',
              fontWeight: 700,
              color: brand.text,
              margin: 0,
              letterSpacing: '-0.02em',
              fontStyle: 'normal',
            }}
          >
            {isDon
              ? 'Sudetenstr. 60 · Hürth'
              : `${contact.street} · ${contact.postalCity.replace(/^\d+\s*/, '')}`}
          </h2>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 24,
          }}
        >
          {(() => {
            const c = isDon ? branchContact('huerth') : contact;
            return [
              {
                icon: '📍',
                title: langKey === 'de' ? 'Adresse' : 'Address',
                value: `${c.street}\n${c.postalCity}`,
                cta: langKey === 'de' ? 'Route planen' : 'Directions',
                href: `https://maps.google.com/?q=${encodeURIComponent(`${c.street}, ${c.postalCity}`)}`,
              },
              {
                icon: '📞',
                title: langKey === 'de' ? 'Anrufen' : 'Call',
                value: c.phone,
                cta: langKey === 'de' ? 'Direkt anrufen' : 'Call now',
                href: `tel:${c.tel}`,
              },
            ];
          })().map((c, i) => (
            <div
              key={i}
              className="lp-card-hover"
              style={{
                padding: 32,
                borderRadius: 20,
                background: brand.bgElev,
                border: `1px solid ${brand.text}10`,
              }}
            >
              <div style={{ fontSize: '2rem', marginBottom: 16 }}>{c.icon}</div>
              <div
                style={{
                  color: brand.textMuted,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  marginBottom: 10,
                }}
              >
                {c.title}
              </div>
              <div
                style={{
                  color: brand.text,
                  fontSize: '1.02rem',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-line',
                  marginBottom: c.cta ? 18 : 0,
                }}
              >
                {c.value}
              </div>
              {c.cta && c.href && (
                <a
                  href={c.href}
                  target={c.href.startsWith('tel:') ? undefined : '_blank'}
                  rel="noopener noreferrer"
                  className="lp-link-underline"
                  style={{
                    color: brand.primary,
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    textDecoration: 'none',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <span>{c.cta}</span>
                  <span>→</span>
                </a>
              )}
            </div>
          ))}

          {/* ─── Opening hours — compact card: live today status only.
               Weekly hours live in the footer; future closures live in the
               footer notice. This card flags today's event, if any. ─────── */}
          {(() => {
            const nowIso = (() => {
              const n = new Date();
              return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
            })();
            const todayOverride = schedule.overrides.find((o) => o.date === nowIso);
            const todayKey = (['sun','mon','tue','wed','thu','fri','sat'] as const)[new Date().getDay()];
            const todayHours = todayOverride
              ? (todayOverride.closed || !(todayOverride.slots && todayOverride.slots.length > 0)
                  ? (langKey === 'de' ? 'Geschlossen' : 'Closed')
                  : todayOverride.slots!.map((s) => `${s.open} – ${s.close}`).join(' · '))
              : formatSlots(schedule.weekly[todayKey], langKey);
            return (
              <div
                className="lp-card-hover"
                style={{
                  padding: 32,
                  borderRadius: 20,
                  background: brand.bgElev,
                  border: `1px solid ${brand.text}10`,
                }}
              >
                <div style={{ fontSize: '2rem', marginBottom: 16 }}>🕐</div>
                <div
                  style={{
                    color: brand.textMuted,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.3em',
                    textTransform: 'uppercase',
                    marginBottom: 12,
                  }}
                >
                  {langKey === 'de' ? 'Öffnungszeiten' : 'Opening hours'}
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span
                    style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: openState.open ? '#4CAF50' : '#ef5350',
                      boxShadow: openState.open ? '0 0 10px #4CAF5090' : 'none',
                      animation: openState.open ? 'brandPulse 2s infinite' : 'none',
                    }}
                  />
                  <span style={{ color: openState.open ? brand.text : '#ef5350', fontWeight: 700, fontSize: '0.98rem' }}>
                    {openState.label[langKey]}
                  </span>
                </div>
                <div style={{ color: brand.textMuted, fontSize: '0.9rem' }}>
                  {langKey === 'de' ? 'Heute' : 'Today'} · {todayHours}
                </div>

                {todayOverride && (
                  <div
                    style={{
                      marginTop: 18,
                      padding: '12px 14px',
                      borderRadius: 12,
                      background: `${brand.primary}14`,
                      border: `1px solid ${brand.primary}38`,
                    }}
                  >
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      fontSize: '0.66rem', fontWeight: 700,
                      letterSpacing: '0.2em', textTransform: 'uppercase',
                      color: brand.primary,
                      marginBottom: 6,
                    }}>
                      <span>⚠</span>
                      <span>{langKey === 'de' ? 'Hinweis heute' : 'Today’s notice'}</span>
                    </div>
                    <div style={{ color: brand.text, fontSize: '0.9rem', lineHeight: 1.4, fontWeight: 600 }}>
                      {todayOverride.reason[langKey] || (todayOverride.closed
                        ? (langKey === 'de' ? 'Heute geschlossen' : 'Closed today')
                        : (langKey === 'de' ? 'Sonderöffnungszeiten' : 'Special hours'))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          FOOTER — premium, multi-column
         ═══════════════════════════════════════════════ */}
      <footer
        style={{
          padding: '80px 24px 32px',
          background: `linear-gradient(180deg, ${brand.bgElev} 0%, ${brand.bg} 100%)`,
          borderTop: `1px solid ${brand.text}10`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle brand-color glow */}
        <div
          style={{
            position: 'absolute',
            top: '-50%', left: '50%', transform: 'translateX(-50%)',
            width: 800, height: 400,
            background: `radial-gradient(ellipse, ${brand.primary}0d 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        <div
          style={{
            maxWidth: 1200, margin: '0 auto',
            position: 'relative', zIndex: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 48,
            marginBottom: 56,
          }}
        >
          {/* Brand column */}
          <div>
            <div style={{ marginBottom: 20 }}>
              <img
                src={brand.id === 'donvito' ? '/donvito_logo.svg?v=2' : brand.logo}
                alt={brand.name}
                style={{
                  height: 128,
                  objectFit: 'contain',
                  filter: `drop-shadow(0 4px 12px ${brand.primary}30)`,
                }}
              />
            </div>
            <p
              style={{
                color: brand.textMuted,
                fontSize: '0.92rem',
                lineHeight: 1.6,
                margin: '0 0 20px',
                fontStyle: 'normal',
                maxWidth: 280,
              }}
            >
              {brand.tagline[langKey]}
            </p>

            {/* Social icons */}
            <div style={{ display: 'flex', gap: 10 }}>
              {[
                {
                  name: 'Instagram',
                  href: 'https://instagram.com',
                  svg: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                    </svg>
                  ),
                },
                {
                  name: 'Facebook',
                  href: 'https://facebook.com',
                  svg: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.5-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z" />
                    </svg>
                  ),
                },
                {
                  name: 'Google',
                  href: 'https://www.google.com/maps/search/?api=1&query=Sudetenstr.+60,+50354+Hürth',
                  svg: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  ),
                },
              ].map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  style={{
                    width: 40, height: 40,
                    borderRadius: '50%',
                    background: `${brand.text}0d`,
                    border: `1px solid ${brand.text}18`,
                    color: brand.textMuted,
                    display: 'grid', placeItems: 'center',
                    transition: 'all .25s cubic-bezier(.22,1,.36,1)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = brand.primary;
                    e.currentTarget.style.color = '#0a0a0a';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.borderColor = brand.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = `${brand.text}0d`;
                    e.currentTarget.style.color = brand.textMuted;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = `${brand.text}18`;
                  }}
                >
                  {s.svg}
                </a>
              ))}
            </div>
          </div>

          {/* Restaurant column */}
          <div>
            <h4
              style={{
                color: brand.text,
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                margin: '0 0 20px',
              }}
            >
              {langKey === 'de' ? 'Restaurant' : 'Restaurant'}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div style={{ color: brand.textMuted, fontSize: '0.72rem', marginBottom: 4 }}>
                  {langKey === 'de' ? 'Adresse' : 'Address'}
                </div>
                <a
                  href="https://maps.google.com/?q=Sudetenstr.+60,+50354+Hürth"
                  target="_blank" rel="noopener noreferrer"
                  style={{
                    color: brand.text,
                    fontSize: '0.92rem',
                    lineHeight: 1.5,
                    textDecoration: 'none',
                  }}
                  className="lp-link-underline"
                >
                  Sudetenstr. 60<br />50354 Hürth
                </a>
              </div>
              <div>
                <div style={{ color: brand.textMuted, fontSize: '0.72rem', marginBottom: 4 }}>
                  {langKey === 'de' ? 'Telefon' : 'Phone'}
                </div>
                <a
                  href="tel:+4922337063324"
                  style={{ color: brand.text, fontSize: '0.92rem', textDecoration: 'none' }}
                  className="lp-link-underline"
                >
                  02233 7063324
                </a>
              </div>
              <div>
                <div style={{ color: brand.textMuted, fontSize: '0.72rem', marginBottom: 4 }}>
                  E-Mail
                </div>
                <a
                  href="mailto:hallo@sushiki.de"
                  style={{ color: brand.text, fontSize: '0.92rem', textDecoration: 'none' }}
                  className="lp-link-underline"
                >
                  hallo@sushiki.de
                </a>
              </div>
            </div>
          </div>

          {/* Opening hours column */}
          <div>
            <h4
              style={{
                color: brand.text,
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                margin: '0 0 20px',
              }}
            >
              {langKey === 'de' ? 'Öffnungszeiten' : 'Opening Hours'}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {DAY_KEYS.map((k) => {
                const d = schedule.weekly[k];
                const closed = d.closed || d.slots.length === 0;
                const lines = slotLines(d, langKey);
                return (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, fontSize: '0.86rem' }}>
                    <span style={{ color: closed ? brand.textMuted : brand.text, flexShrink: 0 }}>{dayLabel(k, langKey)}</span>
                    <span
                      style={{
                        color: closed ? '#ef5350' : brand.textMuted,
                        fontWeight: closed ? 600 : 400,
                        textAlign: 'right',
                        whiteSpace: 'pre-line',
                        lineHeight: 1.4,
                      }}
                    >
                      {lines.join('\n')}
                    </span>
                  </div>
                );
              })}
              {upcomingClosure && (
                <div
                  style={{
                    marginTop: 14,
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: `${brand.primary}18`,
                    border: `1px solid ${brand.primary}40`,
                    fontSize: '0.8rem',
                    color: brand.text,
                    lineHeight: 1.45,
                  }}
                >
                  <div style={{ fontWeight: 700, color: brand.primary, marginBottom: 2 }}>
                    {langKey === 'de' ? 'Hinweis' : 'Notice'}
                  </div>
                  {langKey === 'de'
                    ? `Geschlossen am ${formatOverrideDate(upcomingClosure.date, 'de')}${upcomingClosure.reason.de ? ` — ${upcomingClosure.reason.de}` : ''}`
                    : `Closed on ${formatOverrideDate(upcomingClosure.date, 'en')}${upcomingClosure.reason.en ? ` — ${upcomingClosure.reason.en}` : ''}`}
                </div>
              )}
            </div>
          </div>

          {/* Quick links column */}
          <div>
            <h4
              style={{
                color: brand.text,
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                margin: '0 0 20px',
              }}
            >
              {langKey === 'de' ? 'Schnellzugriff' : 'Quick Links'}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: langKey === 'de' ? 'Speisekarte' : 'Menu', onClick: () => goToMenu('delivery') },
                { label: langKey === 'de' ? 'Bestellen' : 'Order now', onClick: () => goToMenu('delivery') },
                { label: langKey === 'de' ? 'Meine Bestellungen' : 'My orders', onClick: () => navigate('/orders') },
                { label: langKey === 'de' ? 'Mein Konto' : 'My account', onClick: () => navigate(user ? '/account' : '/auth') },
                {
                  label: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      {langKey === 'de' ? 'Auch' : 'Also'}
                      <img
                        src={other.id === 'donvito' ? '/Pizza_Don_Vito_mark_hd_light.png' : '/sushiki_mark_white.png'}
                        alt=""
                        style={{ height: 18, width: 'auto', display: 'inline-block', verticalAlign: 'middle' }}
                      />
                      {other.id === 'donvito' ? 'Pizza' : 'Sushi'}
                    </span>
                  ),
                  onClick: () => switchTo(other.id),
                  accent: true,
                },
              ].map((l, i) => (
                <button
                  key={i}
                  onClick={l.onClick}
                  className="lp-link-underline"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: l.accent ? brand.primary : brand.text,
                    fontSize: '0.92rem',
                    textAlign: 'left',
                    padding: 0,
                    cursor: 'pointer',
                    fontWeight: l.accent ? 700 : 400,
                  }}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          style={{
            maxWidth: 1200, margin: '0 auto',
            paddingTop: 32,
            borderTop: `1px solid ${brand.text}10`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
            position: 'relative', zIndex: 1,
          }}
        >
          <p style={{ color: brand.textMuted, fontSize: '0.78rem', margin: 0 }}>
            © {new Date().getFullYear()} Sushiki UG · Hürth · {langKey === 'de' ? 'Alle Rechte vorbehalten' : 'All rights reserved'}
          </p>
          <div
            style={{
              display: 'flex', gap: 20,
              flexWrap: 'wrap',
            }}
          >
            {[
              { label: langKey === 'de' ? 'Impressum' : 'Legal Notice', href: '/impressum' },
              { label: langKey === 'de' ? 'Datenschutz' : 'Privacy Policy', href: '/datenschutz' },
              { label: langKey === 'de' ? 'AGB' : 'Terms & Conditions', href: '/agb' },
              { label: langKey === 'de' ? 'Cookies' : 'Cookie Policy', href: '/cookies' },
            ].map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="lp-link-underline"
                style={{
                  color: brand.textMuted,
                  fontSize: '0.78rem',
                  textDecoration: 'none',
                }}
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>

        {/* Developer credit — subtle */}
        <div
          style={{
            textAlign: 'center', marginTop: 40,
            color: brand.textMuted, opacity: 0.5,
            fontSize: '0.7rem',
            position: 'relative', zIndex: 1,
          }}
        >
          {langKey === 'de' ? 'Made with ♥ in Hürth' : 'Made with ♥ in Hürth'}
        </div>
      </footer>

      {/* Floating WhatsApp chat button — branded icon, opens WhatsApp */}
      <a
        href="https://wa.me/4922337063324"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp"
        style={{
          position: 'fixed',
          bottom: 24, right: 24,
          zIndex: 50,
          width: 60, height: 60,
          borderRadius: '50%',
          background: brand.bgElev,
          border: `2px solid ${brand.primary}`,
          display: 'grid', placeItems: 'center',
          boxShadow: `0 12px 30px ${brand.primary}55`,
          transition: 'transform .3s',
          animation: 'brandFloat 3s ease-in-out infinite',
          textDecoration: 'none',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      >
        <img
          src={brand.id === 'donvito' ? '/donvito_mark_light.png' : '/sushiki_mark_white.svg'}
          alt=""
          style={{ width: 36, height: 36, objectFit: 'contain' }}
        />
      </a>
    </div>
  );
};

export default LandingPage;
